import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { KvRow, Panel, btnSecondary, money } from "@/components/admin/ui";

const PERIODS = [["day", "Jour"], ["week", "Semaine"], ["month", "Mois"], ["year", "Année"]];
const GROUPS = [["none", "—"], ["driver", "Par chauffeur"], ["category", "Par catégorie"], ["customer", "Par client"], ["org", "Par compte"]];

export default function AdminFinance() {
  const [period, setPeriod] = useState("month");
  const [groupBy, setGroupBy] = useState("none");
  const [finance, setFinance] = useState(null);
  const [reports, setReports] = useState(null);

  useEffect(() => {
    adminApi.get("/finance", { params: { period, group_by: groupBy } }).then((r) => setFinance(r.data)).catch(() => {});
  }, [period, groupBy]);
  useEffect(() => {
    adminApi.get("/reports").then((r) => setReports(r.data)).catch(() => {});
  }, []);

  const days = reports ? Object.entries(reports.by_day) : [];
  const maxRevenue = Math.max(1, ...days.map(([, v]) => v.revenue));

  return (
    <div className="space-y-4" data-testid="admin-finance">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Pilotage financier</h1>
          <p className="mt-0.5 text-sm text-slate">Revenu − coût chauffeur − frais = marge. Pilotage, pas comptabilité.</p>
        </div>
        <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/finance/export.csv?period=${period}`}
          className={btnSecondary} data-testid="finance-export">
          <Download className="h-4 w-4" strokeWidth={1.5} /> Export CSV
        </a>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="flex gap-1 rounded-md border border-slate/15 bg-white p-1">
          {PERIODS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setPeriod(id)} data-testid={`period-${id}`}
              className={`rounded px-3 py-1.5 text-sm ${period === id ? "bg-miles-ice font-semibold text-miles" : "text-slate hover:text-ink"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 rounded-md border border-slate/15 bg-white p-1">
          {GROUPS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setGroupBy(id)} data-testid={`group-${id}`}
              className={`rounded px-3 py-1.5 text-sm ${groupBy === id ? "bg-miles-ice font-semibold text-miles" : "text-slate hover:text-ink"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <Panel title="Rentabilité" testId="finance-summary" className="lg:col-span-4">
          {finance && (
            <>
              <KvRow label="Revenu"><span className="font-mono font-semibold" data-testid="fin-revenue">{money(finance.revenue)}</span></KvRow>
              <KvRow label="Coût chauffeurs"><span className="font-mono text-red-600">−{money(finance.driver_cost)}</span></KvRow>
              <KvRow label="Frais paiement"><span className="font-mono text-red-600">−{money(finance.payment_fees)}</span></KvRow>
              <KvRow label="Autres coûts"><span className="font-mono text-red-600">−{money(finance.other_costs)}</span></KvRow>
              <KvRow label="Marge brute"><span className="font-mono font-semibold text-emerald-600" data-testid="fin-margin">{money(finance.gross_margin)}</span></KvRow>
              <KvRow label="Panier moyen"><span className="font-mono">{money(finance.avg_basket)}</span></KvRow>
              <KvRow label="Remboursements"><span className="font-mono text-red-600">{money(finance.refunds)}</span></KvRow>
              <KvRow label="Courses">{finance.rides}</KvRow>
            </>
          )}
        </Panel>

        <Panel title={groupBy !== "none" ? "Décomposition" : "Revenus par jour (31 j)"} testId="finance-groups" className="lg:col-span-8">
          {groupBy !== "none" && finance && (
            <div className="space-y-1.5">
              {Object.entries(finance.groups).sort((a, b) => b[1].revenue - a[1].revenue).map(([key, g]) => (
                <div key={key} className="flex items-center justify-between rounded border border-slate/10 px-3 py-2 text-sm">
                  <span className="font-medium text-ink">{key}</span>
                  <span className="font-mono text-xs text-slate">{g.rides} courses</span>
                  <span className="font-mono text-ink">{money(g.revenue)}</span>
                  <span className="font-mono text-xs text-emerald-600">marge {money(g.margin)}</span>
                </div>
              ))}
              {Object.keys(finance.groups).length === 0 && <p className="text-sm text-slate">Aucune donnée sur la période.</p>}
            </div>
          )}
          {groupBy === "none" && reports && (
            <div className="flex h-44 items-end gap-1" data-testid="revenue-bars">
              {days.map(([day, v]) => (
                <div key={day} className="group relative flex-1">
                  <div className="w-full rounded-t bg-miles/80 transition-colors group-hover:bg-miles" style={{ height: `${Math.max(2, (v.revenue / maxRevenue) * 160)}px` }} />
                  <div className="pointer-events-none absolute -top-8 left-1/2 z-10 hidden -translate-x-1/2 rounded bg-ink px-2 py-1 font-mono text-[10px] text-white group-hover:block">
                    {day.slice(5)} · {money(v.revenue)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {reports && (
          <>
            <Panel title="Réservations" testId="report-bookings" className="lg:col-span-4">
              <KvRow label="Total">{reports.bookings.total}</KvRow>
              <KvRow label="Terminées">{reports.bookings.completed}</KvRow>
              <KvRow label="Annulées">{reports.bookings.cancelled}</KvRow>
              <KvRow label="Taux de complétion"><span className="font-mono">{reports.bookings.completion_rate} %</span></KvRow>
            </Panel>
            <Panel title="Revenus par canal" testId="report-revenue" className="lg:col-span-4">
              <KvRow label="Direct (site)"><span className="font-mono">{money(reports.revenue.direct)}</span></KvRow>
              <KvRow label="MILES Business"><span className="font-mono">{money(reports.revenue.business)}</span></KvRow>
            </Panel>
            <Panel title="Clients" testId="report-customers" className="lg:col-span-4">
              <KvRow label="Fiches CRM">{reports.customers.total}</KvRow>
              <KvRow label="Clients récurrents">{reports.customers.repeat}</KvRow>
            </Panel>
            <Panel title="Par catégorie" testId="report-categories" className="lg:col-span-4">
              {Object.entries(reports.by_category).map(([k, v]) => <KvRow key={k} label={k}>{v}</KvRow>)}
            </Panel>
            <Panel title="Par chauffeur" testId="report-drivers" className="lg:col-span-8">
              {Object.keys(reports.by_driver).length === 0 && <p className="text-sm text-slate">Attribuez des chauffeurs pour voir cette analyse.</p>}
              {Object.entries(reports.by_driver).sort((a, b) => b[1].revenue - a[1].revenue).map(([name, d]) => (
                <KvRow key={name} label={name}><span className="font-mono">{d.rides} courses · {money(d.revenue)}</span></KvRow>
              ))}
            </Panel>
          </>
        )}
      </div>
    </div>
  );
}
